'use client';
import { useState } from 'react';
import { useT } from '../lib/i18n';
import type { LeftbarLink } from '../lib/site-shared';
import { Icon } from './Icon';
import { SmartLink } from './SmartLink';

const ICON_CHEVRON_LEFT = 'chevron-left';
const ICON_CHEVRON_DOWN = 'chevron-down';
const ICON_EXTERNAL = 'arrow-up-right';

/**
 * 접힘 기억 — **쿠키**다(localStorage 아님, 2026-09-10).
 *
 * 이 저장소가 이미 겪은 문제다(목록 뷰 토글, 2026-08-23 "카드형이었다가 깜박이며 바뀐다") —
 * 브라우저에만 두면 서버는 알 수 없어 **펼친 채로 그린 뒤 접힌다**. 왼쪽 150px 이 통째로
 * 움직이는 자리라 그 깜박임이 크다. 쿠키는 요청과 함께 오므로 첫 HTML 부터 맞는다.
 */
export const GNAV_COOKIE = 'sp_gnav_closed';
const MAX_AGE = 60 * 60 * 24 * 365;

/**
 * 레프트바 — 설정>기능옵션에서 짠 **좌측 고정 메뉴**(가이드 `gnav-*`, 2026-09-10).
 *
 * 현행에도 있던 것(`config.leftbar` → `L_gnb`)인데 새 프론트에는 관리자 화면만 있고 여기가
 * 비어 있었다. 디자이너가 가이드에 드로어를 올려 그 마크업으로 채운다.
 *
 * ## 두 벌이 아니다
 * 데스크탑은 **왼쪽에 붙은 서랍**(150px, 가장자리 탭으로 접는다), 좁은 화면은 **헤더 아래로
 * 펼쳐지는 아코디언**이다. 가이드가 그렇게 그렸다 — 좁은 화면에서 150px 을 왼쪽에 물리면
 * 본문이 남지 않는다. 항목은 한 배열을 두 자리가 나눠 그린다.
 *
 * ## 본문을 밀지 않는다
 * 서랍은 본문 **위에 겹친다**(2026-09-10 사용자 지시). 한때 몸통 여백으로 밀었는데, 열고
 * 닫을 때마다 페이지 전체가 좌우로 움직여 읽던 자리가 흔들렸다. 가이드도 겹치게 그렸다
 * (`position: fixed`·`z-index: 500`). 가리는 것이 싫으면 가장자리 탭으로 접으면 된다.
 *
 * ## 안 그리는 때
 * 설정을 끈 마켓, 항목이 없는 마켓은 **아무것도 그리지 않는다** — 서버가 빈 배열을 준다.
 *
 * 주소는 서버가 이미 풀어 준다(`leftbar.ts`) — 게시판·프로모션은 번호로 저장돼 있어
 * 화면이 알 수 없다. 풀리지 않은 항목(지워진 게시판)은 `href` 가 없고 누를 수 없게 그린다.
 */
export function LeftNav({ items, siteName, mobileOpen, initialClosed }: {
  items: LeftbarLink[];
  siteName: string | null;
  /** 좁은 화면 아코디언이 열렸나 — 헤더의 토글이 켠다 */
  mobileOpen: boolean;
  /** 서버가 쿠키에서 읽어 준 접힘 — 첫 그림부터 맞춘다 */
  initialClosed: boolean;
}) {
  const t = useT();
  const [closed, setClosed] = useState(initialClosed);
  const toggle = () => {
    const next = !closed;
    setClosed(next);
    document.cookie = `${GNAV_COOKIE}=${next ? '1' : '0'}; path=/; max-age=${MAX_AGE}; samesite=lax`;
  };

  if (items.length === 0) return null;

  return (
    <>
      {/* 데스크탑 — 왼쪽에 붙는 서랍. 가장자리 탭이 열고 닫기를 전담한다(가이드 gnav-toggle-2) */}
      <div className={`gnav-drawer${closed ? ' closed' : ''}`}>
        <div className="gnav-clip">
          <div className="gnav-inner">
            <div className="gnav-logo-row">
              <span className="gnav-logo-name">{siteName ?? ''}</span>
            </div>
            <LeftNavItems items={items} />
          </div>
        </div>
        {/* 여닫이 — 접힌 서랍은 폭이 0 이라 이 단추만 밖에 남는다(가이드 `overflow: visible`) */}
        <button type="button" className="gnav-toggle gnav-toggle-2" onClick={toggle}
          aria-expanded={!closed} aria-label={t(closed ? 'common.expand' : 'common.collapse')}>
          <Icon name={ICON_CHEVRON_LEFT} />
        </button>
      </div>

      {/* 좁은 화면 — 헤더 아래 아코디언. 같은 항목을 다른 자리에 그린다 */}
      <div className={`gnav-mobile-panel${mobileOpen ? ' open' : ''}`}>
        <LeftNavItems items={items} />
      </div>
    </>
  );
}

/** 항목 목록 — 데스크탑 서랍과 좁은 화면 아코디언이 **같은 것**을 쓴다 */
function LeftNavItems({ items }: { items: LeftbarLink[] }) {
  return (
    <>
      {items.map((it, i) => (
        it.children.length > 0
          ? <LeftNavGroup key={`${it.name}-${i}`} item={it} />
          : <LeftNavLink key={`${it.name}-${i}`} item={it} className="gnav-link" />
      ))}
    </>
  );
}

/**
 * 하위가 있는 항목 — 머리를 누르면 펼쳐진다.
 *
 * 높이를 재지 않는다. 가이드 CSS 가 `max-height` 로 펴므로 **넉넉한 상한**을 클래스로 준다
 * (`web.css` 의 `.gnav-group.open > .gnav-group-body`). 실제 높이를 재려면 렌더 중에 ref 를
 * 읽어야 하는데 그것은 리액트가 막는 방식이고, 메뉴 목록은 길어야 스무 줄이라 상한으로 충분하다.
 */
function LeftNavGroup({ item }: { item: LeftbarLink }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`gnav-group${open ? ' open' : ''}`}>
      <div className="gnav-group-head" role="button" tabIndex={0} aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}>
        <span>{item.name}</span>
        <Icon name={ICON_CHEVRON_DOWN} className="gnav-chev" />
      </div>
      <div className="gnav-group-body">
        {item.children.map((c, i) => <LeftNavLink key={`${c.name}-${i}`} item={c} className="gnav-sub-link" />)}
      </div>
    </div>
  );
}

/**
 * 한 줄 — 주소가 없으면 **누를 수 없다**(지워진 게시판·빈 링크).
 * 현행은 `javascript:void(0)` 로 두어 눌러도 아무 일이 없었다. 여기서는 링크를 아예 안 만든다.
 */
function LeftNavLink({ item, className }: { item: LeftbarLink; className: string }) {
  const icon = <Icon name={ICON_EXTERNAL} className="gnav-ext-icon" />;
  if (!item.href) return <span className={className} aria-disabled="true">{item.name}{icon}</span>;
  return (
    <SmartLink className={className} href={item.href} target={item.blank ? '_blank' : undefined}>
      {item.name}{icon}
    </SmartLink>
  );
}
